import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ReactComponent as Logo } from '../logo.svg';
import {
  authApi,
  bankCashApi,
  salesInvoicesApi,
  salesCustomersApi,
  salesPaymentsReceivedApi,
  purchasesBillsApi,
  purchasesVendorsApi,
  purchasesPaymentsMadeApi,
  inventoryProductsApi,
  projectsAllApi,
  hrEmployeesApi,
  hrPayrollApi,
} from '../services/api';
import ChartOfAccountsPage from './accounting/ChartOfAccountsPage';
import JournalEntriesPage from './accounting/JournalEntriesPage';
import GeneralLedgerPage from './accounting/GeneralLedgerPage';
import BankCashPage from './accounting/BankCashPage';
import { CustomersPage } from './sales/customers';
import { InvoicesPage } from './sales/invoices';
import { PaymentsReceivedPage } from './sales/payments-received';
import VendorsPage from './purchases/VendorsPage';
import BillsPage from './purchases/BillsPage';
import PaymentsMadePage from './purchases/PaymentsMadePage';
import EmployeesPage from './hr/EmployeesPage';
import PayrollPage from './hr/PayrollPage';
import AttendancePage from './hr/AttendancePage';
import LeaveCreditSettingsPage from './hr/LeaveCreditSettingsPage';
import GeneralPage from './settings/GeneralPage';
import AccessControlPage from './settings/AccessControlPage';
import AllProjectsPage from './projects/AllProjectsPage';
import ProjectPlanningPage from './projects/ProjectPlanningPage';
import TeamCompositionPage from './projects/TeamCompositionPage';
import TaskTrackerPage from './projects/TaskTrackerPage';
import ProjectMonitoringPage from './projects/ProjectMonitoringPage';
import { InventoryPage } from './inventory';
import ReportsPage from './reports/ReportsPage';

// ----- Accounting sub-menu -----
const accountingSubItems = [
  { label: 'Chart of Accounts', href: '/dashboard/accounting/chart-of-accounts', icon: 'book' },
  { label: 'Journal Entries', href: '/dashboard/accounting/journal-entries', icon: 'document' },
  { label: 'General Ledger', href: '/dashboard/accounting/general-ledger', icon: 'ledger' },
  { label: 'Bank & Cash', href: '/dashboard/accounting/bank-cash', icon: 'bank' },
];

// ----- Sales & CRM sub-menu -----
const salesCrmSubItems = [
  { label: 'Customers', href: '/dashboard/sales/customers', icon: 'people' },
  { label: 'Invoices', href: '/dashboard/sales/invoices', icon: 'invoice' },
  { label: 'Payments Received', href: '/dashboard/sales/payments-received', icon: 'wallet' },
];

// ----- Purchases sub-menu -----
const purchasesSubItems = [
  { label: 'Vendors', href: '/dashboard/purchases/vendors', icon: 'truck' },
  { label: 'Bills', href: '/dashboard/purchases/bills', icon: 'document' },
  { label: 'Payments Made', href: '/dashboard/purchases/payments-made', icon: 'wallet' },
];

// ----- HR & Payroll sub-menu -----
const hrPayrollSubItems = [
  { label: 'Employees', href: '/dashboard/hr/employees', icon: 'people' },
  { label: 'Attendance', href: '/dashboard/hr/attendance', icon: 'clock' },
  { label: 'Payroll', href: '/dashboard/hr/payroll', icon: 'payroll' },
  { label: 'Settings', href: '/dashboard/hr/settings', icon: 'gear' },
];

// ----- Settings sub-menu -----
const settingsSubItems = [
  { label: 'General', href: '/dashboard/settings/general', icon: 'gear' },
  { label: 'Access Control', href: '/dashboard/settings/access-control', icon: 'shield' },
];

// ----- Projects sub-menu -----
const projectsSubItems = [
  { label: 'All Projects', href: '/dashboard/projects/all', icon: 'folder' },
  { label: 'Project Planning', href: '/dashboard/projects/planning', icon: 'planning' },
  { label: 'Team Composition', href: '/dashboard/projects/team-composition', icon: 'people' },
  { label: 'Task Tracker', href: '/dashboard/projects/task-tracker', icon: 'clipboard' },
  { label: 'Project monitoring', href: '/dashboard/projects/monitoring', icon: 'chart-bar' },
];

// ----- First sub-page per module (for "land on first module" redirect) -----
const MODULE_ORDER = ['dashboard', 'accounting', 'sales', 'purchases', 'inventory', 'projects', 'hr', 'reports', 'settings'] as const;
const FIRST_SUB_PAGE: Record<string, string> = {
  dashboard: '/dashboard',
  accounting: '/dashboard/accounting/chart-of-accounts',
  sales: '/dashboard/sales/customers',
  purchases: '/dashboard/purchases/vendors',
  inventory: '/dashboard/inventory',
  projects: '/dashboard/projects/all',
  hr: '/dashboard/hr/employees',
  reports: '/dashboard/reports',
  settings: '/dashboard/settings/general',
};

// ----- Nav items (moduleKey matches Roles & Permissions module keys) -----
const navItems = [
  { label: 'Dashboard', icon: 'grid', href: '/dashboard', moduleKey: 'dashboard' as const },
  { label: 'Accounting', icon: 'calculator', href: '#', hasSub: true, moduleKey: 'accounting' as const },
  { label: 'Sales & CRM', icon: 'sales', href: '#', hasSub: true, moduleKey: 'sales' as const },
  { label: 'Purchases', icon: 'cart', href: '#', hasSub: true, moduleKey: 'purchases' as const },
  { label: 'Inventory', icon: 'box', href: '/dashboard/inventory', moduleKey: 'inventory' as const },
  { label: 'Projects', icon: 'folder', href: '#', hasSub: true, moduleKey: 'projects' as const },
  { label: 'HR & Payroll', icon: 'person', href: '#', hasSub: true, moduleKey: 'hr' as const },
  { label: 'Reports', icon: 'chart-bar', href: '/dashboard/reports', moduleKey: 'reports' as const },
  { label: 'Settings', icon: 'gear', href: '#', hasSub: true, moduleKey: 'settings' as const },
];

const iconMap: Record<string, React.ReactNode> = {
  grid: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  ),
  calculator: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  book: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  document: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  ledger: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  bank: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  sales: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  people: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  invoice: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  wallet: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  cart: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  truck: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 17a2 2 0 11-4 0 2 2 0 014 0zM16 17a2 2 0 11-4 0 2 2 0 014 0zM5.5 17H4.5a2 2 0 01-2-2v-6a2 2 0 012-2h10a2 2 0 012 2v2m-2 4h.01" />
    </svg>
  ),
  box: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  folder: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  ),
  person: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  payroll: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  clock: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  'chart-bar': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  gear: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  shield: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  clipboard: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  planning: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
};

// ----- Dashboard static quick actions -----
const quickActions = [
  { title: 'Create Invoice', desc: 'Bill your customers', icon: 'invoice', href: '/dashboard/sales/invoices' },
  { title: 'Record Expense', desc: 'Track spending', icon: 'expense', href: '/dashboard/accounting/bank-cash' },
  { title: 'Add Customer', desc: 'New contact', icon: 'customer', href: '/dashboard/sales/customers' },
  { title: 'Journal Entry', desc: 'Manual adjustments', icon: 'journal', href: '/dashboard/accounting/journal-entries' },
];

// ----- Dashboard aggregated data (loaded from APIs) -----
function formatPeso(n: number): string {
  if (n >= 1_000_000) return `₱${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `₱${(n / 1_000).toFixed(1)}k`;
  return `₱${Number(n).toFixed(0)}`;
}

export interface DashboardData {
  totalRevenue: number;
  totalExpenses: number;
  receivables: number;
  activeCustomers: number;
  cashFlowMonths: string[];
  cashFlowIncome: number[];
  cashFlowExpenses: number[];
  overdueInvoices: { id: string; invoice_number: string; customer_name: string; balance_due: number; due_date: string }[];
  recentActivity: { type: string; detail: string; time: string; module: string }[];
  vendorsCount: number;
  overdueBillsCount: number;
  overdueBills: { id: string; bill_number: string; vendor_name: string; balance_due: number; due_date: string }[];
  inventoryStats: { total_products: number; low_stock: number; out_of_stock: number; stock_value: number } | null;
  projectStats: { total_projects: number; active_projects: number; total_budget: number; revenue: number } | null;
  hrStats: { employeesCount: number; monthlyPayroll: number; nextPayDate: string | null } | null;
}

async function loadDashboardData(): Promise<DashboardData> {
  const now = new Date();
  const thisYear = now.getFullYear();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const last6Months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(thisYear, now.getMonth() - 5 + i, 1);
    return { label: monthNames[d.getMonth()], year: d.getFullYear(), month: d.getMonth() };
  });

  const firstMonth = new Date(thisYear, now.getMonth() - 5, 1);
  const lastDayOfCurrentMonth = new Date(thisYear, now.getMonth() + 1, 0);
  const fromDate = firstMonth.toISOString().slice(0, 10);
  const toDate = lastDayOfCurrentMonth.toISOString().slice(0, 10);

  const [
    invoicesRes,
    customersRes,
    paymentsReceivedRes,
    billsRes,
    vendorsRes,
    paymentsMadeRes,
    bankCashRes,
    inventoryRes,
    projectsRes,
    employeesRes,
    payrollRes,
  ] = await Promise.allSettled([
    salesInvoicesApi.list(),
    salesCustomersApi.list(),
    salesPaymentsReceivedApi.list(),
    purchasesBillsApi.list(),
    purchasesVendorsApi.list(),
    purchasesPaymentsMadeApi.list(),
    bankCashApi.getTransactions({ from_date: fromDate, to_date: toDate }),
    inventoryProductsApi.list(),
    projectsAllApi.list(),
    hrEmployeesApi.list(),
    hrPayrollApi.getSummaryAndRuns(),
  ]);

  const invoices = invoicesRes.status === 'fulfilled' && invoicesRes.value.data ? invoicesRes.value.data : [];
  const customers = customersRes.status === 'fulfilled' && customersRes.value.data ? customersRes.value.data : [];
  const paymentsReceived =
    paymentsReceivedRes.status === 'fulfilled' && paymentsReceivedRes.value.data ? paymentsReceivedRes.value.data : [];
  const bills = billsRes.status === 'fulfilled' && billsRes.value.data ? billsRes.value.data : [];
  const vendors = vendorsRes.status === 'fulfilled' && vendorsRes.value.data ? vendorsRes.value.data : [];
  const paymentsMade =
    paymentsMadeRes.status === 'fulfilled' && paymentsMadeRes.value.data ? paymentsMadeRes.value.data : [];
  const bankCashTransactions =
    bankCashRes.status === 'fulfilled' && bankCashRes.value.data ? bankCashRes.value.data : [];
  const inventoryStats =
    inventoryRes.status === 'fulfilled' && inventoryRes.value.stats != null ? inventoryRes.value.stats : null;
  const projectStats =
    projectsRes.status === 'fulfilled' && projectsRes.value.stats != null ? projectsRes.value.stats : null;
  const employees = employeesRes.status === 'fulfilled' && employeesRes.value.data ? employeesRes.value.data : [];
  const payrollSummary =
    payrollRes.status === 'fulfilled' && payrollRes.value.data?.summary != null ? payrollRes.value.data.summary : null;

  const totalRevenue = paymentsReceived.reduce((s, p) => s + p.amount, 0);
  const bankCashWithdrawals = bankCashTransactions
    .filter((t) => (t.type ?? '').toLowerCase() === 'withdrawal')
    .reduce((s, t) => s + t.amount, 0);
  const totalExpenses = paymentsMade.reduce((s, p) => s + p.amount, 0) + bankCashWithdrawals;
  const receivables = invoices.reduce((s, inv) => s + (inv.balance_due ?? 0), 0);
  const today = new Date().toISOString().slice(0, 10);
  const overdueInvoices = invoices
    .filter((inv) => inv.status === 'overdue' || (inv.due_date < today && (inv.balance_due ?? 0) > 0))
    .map((inv) => ({
      id: inv.id,
      invoice_number: inv.invoice_number,
      customer_name: inv.customer_name,
      balance_due: inv.balance_due ?? 0,
      due_date: inv.due_date,
    }))
    .slice(0, 5);

  // Parse Y-m-d date string to year/month (avoids timezone shifting)
  const parseDateParts = (dateStr: string): { year: number; month: number } | null => {
    if (!dateStr) return null;
    const parts = String(dateStr).split('-').map(Number);
    if (parts.length < 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    return { year: parts[0], month: parts[1] - 1 }; // month 0-indexed for comparison
  };
  const incomeByMonth = last6Months.map(({ year, month }) => {
    const fromPayments = paymentsReceived
      .filter((p) => {
        const d = new Date(p.payment_date);
        return d.getFullYear() === year && d.getMonth() === month;
      })
      .reduce((s, p) => s + p.amount, 0);
    const fromBankCash = bankCashTransactions
      .filter((t) => {
        const parts = parseDateParts(t.date);
        return parts && (t.type ?? '').toLowerCase() === 'deposit' && parts.year === year && parts.month === month;
      })
      .reduce((s, t) => s + t.amount, 0);
    return fromPayments + fromBankCash;
  });
  const expensesByMonth = last6Months.map(({ year, month }) => {
    const fromPayments = paymentsMade
      .filter((p) => {
        const d = new Date(p.payment_date);
        return d.getFullYear() === year && d.getMonth() === month;
      })
      .reduce((s, p) => s + p.amount, 0);
    const fromBankCash = bankCashTransactions
      .filter((t) => {
        const parts = parseDateParts(t.date);
        return parts && (t.type ?? '').toLowerCase() === 'withdrawal' && parts.year === year && parts.month === month;
      })
      .reduce((s, t) => s + t.amount, 0);
    return fromPayments + fromBankCash;
  });
  const maxVal = Math.max(1, ...incomeByMonth, ...expensesByMonth);

  const overdueBills = bills.filter((b) => b.due_date < today && (b.balance_due ?? 0) > 0);
  const overdueBillsList = overdueBills.slice(0, 5).map((b) => ({
    id: b.id,
    bill_number: b.bill_number,
    vendor_name: b.vendor_name,
    balance_due: b.balance_due ?? 0,
    due_date: b.due_date,
  }));

  type ActivityItem = { type: string; detail: string; time: string; module: string; sortKey: string };
  const activities: ActivityItem[] = [];
  invoices.slice(0, 5).forEach((inv) => {
    activities.push({
      type: 'Invoice',
      detail: `${inv.invoice_number} – ${inv.customer_name}`,
      time: inv.invoice_date,
      module: 'sales',
      sortKey: inv.invoice_date + inv.id,
    });
  });
  bills.slice(0, 5).forEach((b) => {
    activities.push({
      type: 'Bill',
      detail: `${b.bill_number} – ${b.vendor_name}`,
      time: b.bill_date,
      module: 'purchases',
      sortKey: b.bill_date + b.id,
    });
  });
  paymentsReceived.slice(0, 5).forEach((p) => {
    activities.push({
      type: 'Payment received',
      detail: `${p.customer_name} – ${formatPeso(p.amount)}`,
      time: p.payment_date,
      module: 'sales',
      sortKey: p.payment_date + p.id,
    });
  });
  paymentsMade.slice(0, 5).forEach((p) => {
    activities.push({
      type: 'Payment made',
      detail: `${p.vendor_name} – ${formatPeso(p.amount)}`,
      time: p.payment_date,
      module: 'purchases',
      sortKey: p.payment_date + p.id,
    });
  });
  bankCashTransactions.slice(0, 10).forEach((t) => {
    activities.push({
      type: (t.type ?? '').toLowerCase() === 'deposit' ? 'Bank deposit' : 'Bank withdrawal',
      detail: `${t.accountName} – ${formatPeso(t.amount)}${t.description ? ` · ${t.description}` : ''}`,
      time: t.date,
      module: 'accounting',
      sortKey: t.date + t.id,
    });
  });
  activities.sort((a, b) => (b.sortKey > a.sortKey ? 1 : -1));
  const recentActivity = activities.slice(0, 10).map(({ type, detail, time, module }) => ({
    type,
    detail,
    time: formatRelativeTime(time),
    module,
  }));

  function formatRelativeTime(iso: string): string {
    const d = new Date(iso);
    const diff = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
    if (diff < 1) return 'Today';
    if (diff < 2) return 'Yesterday';
    if (diff < 7) return `${Math.floor(diff)} days ago`;
    return d.toLocaleDateString();
  }

  return {
    totalRevenue,
    totalExpenses,
    receivables,
    activeCustomers: customers.length,
    cashFlowMonths: last6Months.map((m) => m.label),
    cashFlowIncome: incomeByMonth,
    cashFlowExpenses: expensesByMonth,
    overdueInvoices,
    recentActivity,
    vendorsCount: vendors.length,
    overdueBillsCount: overdueBills.length,
    overdueBills: overdueBillsList,
    inventoryStats,
    projectStats,
    hrStats: {
      employeesCount: employees.length,
      monthlyPayroll: payrollSummary?.monthly_payroll ?? 0,
      nextPayDate: payrollSummary?.next_pay_date ?? null,
    },
  };
}

function getStoredCompanyName(): string {
  try {
    const stored = localStorage.getItem('user_data');
    if (stored) {
      const user = JSON.parse(stored);
      if (user?.merchant_name) return user.merchant_name;
    }
  } catch {
    /* ignore */
  }
  return 'My Company';
}

function getStoredUserInitial(): string {
  try {
    const stored = localStorage.getItem('user_data');
    if (stored) {
      const user = JSON.parse(stored);
      const name = user?.first_name || user?.name || '';
      if (name) return name.charAt(0).toUpperCase();
    }
  } catch {
    /* ignore */
  }
  return 'U';
}

function DashboardIndexWrapper({
  allowedModules,
  scale,
}: {
  allowedModules: string[] | null | undefined;
  scale: (v: number) => number;
}) {
  // Merchant/owner (null) or loading (undefined): show dashboard overview
  if (allowedModules === undefined || allowedModules === null) {
    return <DashboardHome scale={scale} />;
  }
  // Restricted user: redirect to first allowed module's first sub-page
  const firstModule = MODULE_ORDER.find((key) => allowedModules.includes(key));
  const firstPath = firstModule ? FIRST_SUB_PAGE[firstModule] : '/dashboard';
  if (firstPath !== '/dashboard') {
    return <Navigate to={firstPath} replace />;
  }
  return <DashboardHome scale={scale} />;
}

function DashboardHome({ scale }: { scale: (v: number) => number }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadDashboardData()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const summaryCardsFromData = data
    ? [
        {
          label: 'Total Revenue',
          value: formatPeso(data.totalRevenue),
          change: '—',
          trend: 'neutral' as const,
          iconBg: 'bg-emerald-100',
          icon: 'dollar' as const,
        },
        {
          label: 'Total Expenses',
          value: formatPeso(data.totalExpenses),
          change: '—',
          trend: 'neutral' as const,
          iconBg: 'bg-red-100',
          icon: 'wallet' as const,
        },
        {
          label: 'Receivables',
          value: formatPeso(data.receivables),
          change: '—',
          trend: 'neutral' as const,
          iconBg: 'bg-blue-100',
          icon: 'dollar' as const,
        },
        {
          label: 'Active Customers',
          value: String(data.activeCustomers),
          change: '—',
          trend: 'neutral' as const,
          iconBg: 'bg-gray-200',
          icon: 'people' as const,
        },
      ]
    : [
        { label: 'Total Revenue', value: '₱0', change: '—', trend: 'neutral' as const, iconBg: 'bg-emerald-100', icon: 'dollar' as const },
        { label: 'Total Expenses', value: '₱0', change: '—', trend: 'neutral' as const, iconBg: 'bg-red-100', icon: 'wallet' as const },
        { label: 'Receivables', value: '₱0', change: '—', trend: 'neutral' as const, iconBg: 'bg-blue-100', icon: 'dollar' as const },
        { label: 'Active Customers', value: '0', change: '—', trend: 'neutral' as const, iconBg: 'bg-gray-200', icon: 'people' as const },
      ];

  const chartScale =
    data && data.cashFlowIncome.length
      ? (v: number) => (v / Math.max(1, ...data.cashFlowIncome, ...data.cashFlowExpenses)) * 80
      : scale;

  const cashMonths = data?.cashFlowMonths ?? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const cashIncome = data?.cashFlowIncome ?? [0, 0, 0, 0, 0, 0];
  const cashExpenses = data?.cashFlowExpenses ?? [0, 0, 0, 0, 0, 0];
  const chartMax = Math.max(1, ...cashIncome, ...cashExpenses);

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <h1 className="text-lg font-bold text-gray-900 mb-3 shrink-0">Merchant Dashboard</h1>
      {loading && !data && (
        <div className="flex items-center justify-center py-8 text-gray-500 text-sm">Loading dashboard…</div>
      )}
      {/* Summary cards - Accounting & Sales */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3 shrink-0">
        {summaryCardsFromData.map((card, i) => (
          <motion.div
            key={card.label}
            className="bg-white rounded-xl p-3 shadow-card border border-gray-100"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm text-gray-500 mb-0.5">{card.label}</p>
                <p className="text-xl font-bold text-gray-900">{card.value}</p>
                <p className="text-sm mt-1 flex items-center gap-0.5 text-gray-500">
                  {card.change} {card.trend !== 'neutral' ? 'vs last month' : ''}
                </p>
              </div>
              <span
                className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                  card.icon === 'dollar' ? card.iconBg : card.icon === 'wallet' ? 'bg-red-100' : 'bg-gray-200'
                }`}
              >
                {card.icon === 'dollar' || card.icon === 'wallet' ? (
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )}
              </span>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Module summary cards - Purchases, Inventory, Projects, HR */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3 shrink-0">
        <motion.div
          className="bg-white rounded-xl p-3 shadow-card border border-gray-100"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-0.5">Vendors</p>
              <p className="text-xl font-bold text-gray-900">{data?.vendorsCount ?? '—'}</p>
              <p className="text-xs text-gray-500 mt-1">
                {data ? `${data.overdueBillsCount} overdue bills` : '—'}
              </p>
            </div>
            <Link to="/dashboard/purchases/vendors" className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center shrink-0 text-amber-600 hover:bg-amber-200">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 17a2 2 0 11-4 0 2 2 0 014 0zM16 17a2 2 0 11-4 0 2 2 0 014 0zM5.5 17H4.5a2 2 0 01-2-2v-6a2 2 0 012-2h10a2 2 0 012 2v2m-2 4h.01" />
              </svg>
            </Link>
          </div>
        </motion.div>
        <motion.div
          className="bg-white rounded-xl p-3 shadow-card border border-gray-100"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-0.5">Inventory</p>
              <p className="text-xl font-bold text-gray-900">
                {data?.inventoryStats != null ? data.inventoryStats.total_products : '—'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {data?.inventoryStats != null
                  ? `${data.inventoryStats.low_stock} low stock · ${formatPeso(data.inventoryStats.stock_value)} value`
                  : '—'}
              </p>
            </div>
            <Link to="/dashboard/inventory" className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center shrink-0 text-sky-600 hover:bg-sky-200">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </Link>
          </div>
        </motion.div>
        <motion.div
          className="bg-white rounded-xl p-3 shadow-card border border-gray-100"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-0.5">Projects</p>
              <p className="text-xl font-bold text-gray-900">
                {data?.projectStats != null ? data.projectStats.active_projects : '—'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {data?.projectStats != null
                  ? `${data.projectStats.total_projects} total · ${formatPeso(data.projectStats.total_budget)} budget`
                  : '—'}
              </p>
            </div>
            <Link to="/dashboard/projects/all" className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0 text-violet-600 hover:bg-violet-200">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </Link>
          </div>
        </motion.div>
        <motion.div
          className="bg-white rounded-xl p-3 shadow-card border border-gray-100"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-500 mb-0.5">HR & Payroll</p>
              <p className="text-xl font-bold text-gray-900">{data?.hrStats != null ? data.hrStats.employeesCount : '—'}</p>
              <p className="text-xs text-gray-500 mt-1">
                {data?.hrStats != null
                  ? `${formatPeso(data.hrStats.monthlyPayroll)}/mo${data.hrStats.nextPayDate ? ` · Next pay: ${data.hrStats.nextPayDate}` : ''}`
                  : '—'}
              </p>
            </div>
            <Link to="/dashboard/hr/employees" className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center shrink-0 text-teal-600 hover:bg-teal-200">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </Link>
          </div>
        </motion.div>
      </div>
      {/* Cash Flow + Quick Actions - stack on mobile to prevent overlap */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3 flex-1 min-h-0 overflow-hidden">
        <div className="lg:col-span-2 bg-white rounded-xl p-3 shadow-card border border-gray-100 flex flex-col min-h-[200px] lg:min-h-[180px] order-1">
          <h2 className="font-semibold text-gray-900 text-sm mb-0.5">Cash Flow</h2>
          <p className="text-xs text-gray-500 mb-2">Income vs Expenses</p>
          <div className="flex flex-wrap gap-3 mb-2">
            <span className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-full bg-brand-500 shrink-0" />
              Income
            </span>
            <span className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-full bg-red-500 shrink-0" />
              Expenses
            </span>
          </div>
          <div className="flex-1 min-h-[100px] relative w-full">
            <svg viewBox="0 0 400 120" className="w-full h-full min-h-[100px]" preserveAspectRatio="xMidYMid meet">
              <defs>
                <linearGradient id="incomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="expenseGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                </linearGradient>
              </defs>
              {cashMonths.map((_, i) => {
                const x = (i / Math.max(1, cashMonths.length - 1)) * 400;
                const incomeY = 100 - chartScale(cashIncome[i] ?? 0);
                const expenseY = 100 - chartScale(cashExpenses[i] ?? 0);
                const prevIncomeY = i > 0 ? 100 - chartScale(cashIncome[i - 1] ?? 0) : incomeY;
                const prevExpenseY = i > 0 ? 100 - chartScale(cashExpenses[i - 1] ?? 0) : expenseY;
                const prevX = i > 0 ? ((i - 1) / Math.max(1, cashMonths.length - 1)) * 400 : 0;
                return (
                  <g key={i}>
                    {i > 0 && (
                      <>
                        <path
                          d={`M${prevX} ${prevIncomeY} L${x} ${incomeY} L${x} 100 L${prevX} 100 Z`}
                          fill="url(#incomeGrad)"
                        />
                        <path
                          d={`M${prevX} ${prevExpenseY} L${x} ${expenseY} L${x} 100 L${prevX} 100 Z`}
                          fill="url(#expenseGrad)"
                        />
                        <path
                          d={`M${prevX} ${prevIncomeY} L${x} ${incomeY}`}
                          fill="none"
                          stroke="#7c3aed"
                          strokeWidth={2}
                        />
                        <path
                          d={`M${prevX} ${prevExpenseY} L${x} ${expenseY}`}
                          fill="none"
                          stroke="#ef4444"
                          strokeWidth={2}
                        />
                      </>
                    )}
                  </g>
                );
              })}
            </svg>
            <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-gray-400 px-1 pointer-events-none">
              {cashMonths.map((m) => (
                <span key={m}>{m}</span>
              ))}
            </div>
          </div>
          <div className="flex justify-between mt-1 text-xs text-gray-500">
            <span>₱0</span>
            <span>{formatPeso(chartMax)}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl p-3 shadow-card border border-gray-100 flex flex-col min-h-0 overflow-hidden order-2 shrink-0 lg:shrink">
          <h2 className="font-semibold text-gray-900 text-sm mb-2 shrink-0">Quick Actions</h2>
          <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
            {quickActions.map((action) => (
              <Link
                key={action.title}
                to={action.href}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-100 transition-all group"
              >
                <span className="w-8 h-8 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 text-sm">{action.title}</p>
                  <p className="text-xs text-gray-500">{action.desc}</p>
                </div>
                <span className="text-gray-400 group-hover:text-brand-600 group-hover:translate-x-1 transition-all">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
      {/* Recent Activity + Overdue Invoices & Bills - single column on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 flex-1 min-h-0 overflow-auto">
        <div className="bg-white rounded-xl p-3 shadow-card border border-gray-100 flex flex-col min-h-0 overflow-hidden shrink-0">
          <h2 className="font-semibold text-gray-900 text-sm mb-2 shrink-0">Recent Activity</h2>
          {(data?.recentActivity?.length ?? 0) > 0 ? (
            <div className="space-y-3 overflow-y-auto min-h-0 flex-1">
              {(data?.recentActivity ?? []).map((item, idx) => (
                <div key={`${item.type}-${idx}-${item.detail.slice(0, 20)}`} className="flex items-start gap-3">
                  <span className="w-10 h-10 rounded-lg bg-brand-100 text-brand-600 flex items-center justify-center shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="font-medium text-gray-900 text-sm">{item.type}</p>
                    <p className="text-xs text-gray-500 break-words line-clamp-2">{item.detail}</p>
                  </div>
                  <span className="text-xs text-gray-400 shrink-0 whitespace-nowrap">{item.time}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-4 text-center flex-1 min-h-0">
              <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <p className="text-gray-600 font-medium text-sm">No recent activity</p>
              <p className="text-xs text-gray-500 mt-0.5">Activity from invoices, bills, and payments will appear here</p>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3 min-h-0 overflow-hidden">
          <div className="bg-white rounded-xl p-3 shadow-card border border-gray-100 flex flex-col min-h-0 overflow-hidden flex-1 min-h-[140px]">
            <div className="flex items-center justify-between mb-2 shrink-0 gap-2 flex-wrap">
              <h2 className="font-semibold text-gray-900 text-sm">Overdue Invoices</h2>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-red-100 text-red-700 shrink-0">
                {data?.overdueInvoices?.length ?? 0} overdue
              </span>
            </div>
            {(data?.overdueInvoices?.length ?? 0) > 0 ? (
              <div className="space-y-2 overflow-y-auto min-h-0 flex-1">
                {(data?.overdueInvoices ?? []).map((inv) => (
                  <Link
                    key={inv.id}
                    to={`/dashboard/sales/invoices?highlight=${inv.id}`}
                    className="block p-2 rounded-lg border border-gray-100 hover:bg-gray-50 text-left"
                  >
                    <p className="font-medium text-gray-900 text-sm">{inv.invoice_number}</p>
                    <p className="text-xs text-gray-500">{inv.customer_name} · {formatPeso(inv.balance_due)}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-4 text-center flex-1 min-h-0">
                <span className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </span>
                <p className="text-gray-600 font-medium text-sm">No overdue invoices</p>
                <p className="text-xs text-gray-500 mt-0.5">You&apos;re all caught up</p>
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl p-3 shadow-card border border-gray-100 flex flex-col min-h-0 overflow-hidden flex-1 min-h-[140px]">
            <div className="flex items-center justify-between mb-2 shrink-0 gap-2 flex-wrap">
              <h2 className="font-semibold text-gray-900 text-sm">Overdue Bills</h2>
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 shrink-0">
                {data?.overdueBillsCount ?? 0} overdue
              </span>
            </div>
            {(data?.overdueBills?.length ?? 0) > 0 ? (
              <div className="space-y-2 overflow-y-auto min-h-0 flex-1">
                {(data?.overdueBills ?? []).map((b) => (
                  <Link
                    key={b.id}
                    to={`/dashboard/purchases/bills?highlight=${b.id}`}
                    className="block p-2 rounded-lg border border-gray-100 hover:bg-gray-50 text-left"
                  >
                    <p className="font-medium text-gray-900 text-sm">{b.bill_number}</p>
                    <p className="text-xs text-gray-500">{b.vendor_name} · {formatPeso(b.balance_due)}</p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-3 text-center flex-1 min-h-0">
                <p className="text-gray-600 font-medium text-sm">No overdue bills</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [accountingOpen, setAccountingOpen] = useState(false);
  const [salesCrmOpen, setSalesCrmOpen] = useState(false);
  const [purchasesOpen, setPurchasesOpen] = useState(false);
  const [hrPayrollOpen, setHrPayrollOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const accountingNavRef = useRef<HTMLDivElement>(null);
  const [companyDropdown, setCompanyDropdown] = useState(false);
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const [companyName, setCompanyName] = useState(getStoredCompanyName);
  const [userInitial, setUserInitial] = useState(getStoredUserInitial);
  const [allowedModules, setAllowedModules] = useState<string[] | null | undefined>(undefined);
  const isAccountingPath = location.pathname.startsWith('/dashboard/accounting');
  const isSalesCrmPath = location.pathname.startsWith('/dashboard/sales');
  const isPurchasesPath = location.pathname.startsWith('/dashboard/purchases');
  const isHrPayrollPath = location.pathname.startsWith('/dashboard/hr');
  const isProjectsPath = location.pathname.startsWith('/dashboard/projects');
  const isSettingsPath = location.pathname.startsWith('/dashboard/settings');

  useEffect(() => {
    if (isAccountingPath) setAccountingOpen(true);
  }, [isAccountingPath]);

  useEffect(() => {
    if (isSalesCrmPath) setSalesCrmOpen(true);
  }, [isSalesCrmPath]);

  useEffect(() => {
    if (isPurchasesPath) setPurchasesOpen(true);
  }, [isPurchasesPath]);

  useEffect(() => {
    if (isHrPayrollPath) setHrPayrollOpen(true);
  }, [isHrPayrollPath]);

  useEffect(() => {
    if (isProjectsPath) setProjectsOpen(true);
  }, [isProjectsPath]);

  useEffect(() => {
    if (isSettingsPath) setSettingsOpen(true);
  }, [isSettingsPath]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Redirect if user tries to access a module they don't have permission for
  useEffect(() => {
    if (allowedModules === undefined || allowedModules === null) return; // show all (merchant/owner)
    const path = location.pathname;
    const pathToModule: Record<string, string> = {
      '/dashboard': 'dashboard',
      '/dashboard/inventory': 'inventory',
      '/dashboard/reports': 'reports',
    };
    let moduleKey = pathToModule[path];
    if (!moduleKey) {
      if (path.startsWith('/dashboard/accounting')) moduleKey = 'accounting';
      else if (path.startsWith('/dashboard/sales')) moduleKey = 'sales';
      else if (path.startsWith('/dashboard/purchases')) moduleKey = 'purchases';
      else if (path.startsWith('/dashboard/projects')) moduleKey = 'projects';
      else if (path.startsWith('/dashboard/hr')) moduleKey = 'hr';
      else if (path.startsWith('/dashboard/settings')) moduleKey = 'settings';
    }
    if (moduleKey && !allowedModules.includes(moduleKey)) {
      navigate('/dashboard');
    }
  }, [location.pathname, allowedModules, navigate]);

  useEffect(() => {
    authApi.me().then((res) => {
      const user = res?.user;
      if (user?.user_type === 'employee') {
        navigate('/employee');
        return;
      }
      if (user?.merchant_name) {
        setCompanyName(user.merchant_name);
        localStorage.setItem('user_data', JSON.stringify(user));
      }
      const name = user?.first_name || user?.name || '';
      if (name) setUserInitial(name.charAt(0).toUpperCase());
      // Role-based module access: null = show all (merchant/owner), array = only those modules
      setAllowedModules(user?.allowed_modules);
    }).catch(() => {
      /* use stored fallback */
      try {
        const stored = localStorage.getItem('user_data');
        if (stored) {
          const user = JSON.parse(stored);
          setAllowedModules(user?.allowed_modules);
        }
      } catch { /* ignore */ }
    });
  }, [navigate]);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = () => {
      if (mq.matches) setMobileMenuOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(e.target as Node)) {
        setCompanyDropdown(false);
      }
      if (accountingNavRef.current && !accountingNavRef.current.contains(e.target as Node)) {
        setAccountingOpen(false);
        setSalesCrmOpen(false);
        setPurchasesOpen(false);
        setHrPayrollOpen(false);
        setProjectsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [companyDropdown, accountingOpen, salesCrmOpen, purchasesOpen, hrPayrollOpen, projectsOpen]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    navigate('/login');
  };

  const scale = (v: number) => (v / Math.max(v, 1)) * 80;

  const showLabels = sidebarOpen || mobileMenuOpen;

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Mobile backdrop */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close menu"
        className={`fixed inset-0 z-40 bg-black/50 transition-opacity md:hidden ${
          mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
        onKeyDown={(e) => e.key === 'Escape' && setMobileMenuOpen(false)}
      />
      {/* Sidebar: drawer on mobile, inline on desktop */}
      <aside
        className={`fixed md:relative inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-out
          md:shrink-0
          ${mobileMenuOpen ? 'translate-x-0 w-72 max-w-[85vw]' : '-translate-x-full md:translate-x-0'}
          ${sidebarOpen && !mobileMenuOpen ? 'md:w-64' : 'md:w-20'}
        `}
      >
        <div className="p-4 flex items-center gap-2 border-b border-gray-100">
          <span className="w-9 h-9 rounded-lg bg-brand-600 flex items-center justify-center text-white shrink-0">
            <Logo className="w-5 h-5 shrink-0" aria-hidden />
          </span>
          {showLabels && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="overflow-hidden min-w-0"
            >
              <span className="font-bold text-gray-900 block truncate">SpynERP</span>
              <span className="text-xs text-gray-500 block truncate">{companyName}</span>
            </motion.div>
          )}
        </div>
        <nav className="flex-1 p-3 overflow-y-auto" ref={accountingNavRef}>
          {(allowedModules === undefined || allowedModules === null
            ? navItems
            : navItems.filter((item) => allowedModules.includes(item.moduleKey))
          ).map((item) => {
            if (item.hasSub && item.label === 'Accounting') {
              const isOpen = accountingOpen && showLabels;
              return (
                <div key={item.label} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => setAccountingOpen(!accountingOpen)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-colors text-left ${
                      isAccountingPath ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-gray-500 shrink-0">{iconMap[item.icon] || iconMap.grid}</span>
                    {showLabels && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                  {showLabels && isOpen && (
                    <div className="ml-2 mt-0.5 pl-3 border-l-2 border-gray-100 space-y-0.5">
                      {accountingSubItems.map((sub) => {
                        const isActive = location.pathname === sub.href;
                        return (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-500 -ml-[2px] pl-[10px]'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-gray-500 shrink-0">{iconMap[sub.icon]}</span>
                            <span className="flex-1 truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            if (item.hasSub && item.label === 'Sales & CRM') {
              const isOpen = salesCrmOpen && showLabels;
              return (
                <div key={item.label} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => setSalesCrmOpen(!salesCrmOpen)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-colors text-left ${
                      isSalesCrmPath ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-gray-500 shrink-0">{iconMap[item.icon] || iconMap.grid}</span>
                    {showLabels && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                  {showLabels && isOpen && (
                    <div className="ml-2 mt-0.5 pl-3 border-l-2 border-gray-100 space-y-0.5">
                      {salesCrmSubItems.map((sub) => {
                        const isActive = location.pathname === sub.href;
                        return (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-500 -ml-[2px] pl-[10px]'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-gray-500 shrink-0">{iconMap[sub.icon]}</span>
                            <span className="flex-1 truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            if (item.hasSub && item.label === 'Purchases') {
              const isOpen = purchasesOpen && showLabels;
              return (
                <div key={item.label} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => setPurchasesOpen(!purchasesOpen)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-colors text-left ${
                      isPurchasesPath ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-gray-500 shrink-0">{iconMap[item.icon] || iconMap.grid}</span>
                    {showLabels && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                  {showLabels && isOpen && (
                    <div className="ml-2 mt-0.5 pl-3 border-l-2 border-gray-100 space-y-0.5">
                      {purchasesSubItems.map((sub) => {
                        const isActive = location.pathname === sub.href;
                        return (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-500 -ml-[2px] pl-[10px]'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-gray-500 shrink-0">{iconMap[sub.icon]}</span>
                            <span className="flex-1 truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            if (item.hasSub && item.label === 'HR & Payroll') {
              const isOpen = hrPayrollOpen && showLabels;
              return (
                <div key={item.label} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => setHrPayrollOpen(!hrPayrollOpen)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-colors text-left ${
                      isHrPayrollPath ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-gray-500 shrink-0">{iconMap[item.icon] || iconMap.grid}</span>
                    {showLabels && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                  {showLabels && isOpen && (
                    <div className="ml-2 mt-0.5 pl-3 border-l-2 border-gray-100 space-y-0.5">
                      {hrPayrollSubItems.map((sub) => {
                        const isActive = location.pathname === sub.href;
                        return (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-500 -ml-[2px] pl-[10px]'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-gray-500 shrink-0">{iconMap[sub.icon]}</span>
                            <span className="flex-1 truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            if (item.hasSub && item.label === 'Projects') {
              const isOpen = projectsOpen && showLabels;
              return (
                <div key={item.label} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => setProjectsOpen(!projectsOpen)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-colors text-left ${
                      isProjectsPath ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-gray-500 shrink-0">{iconMap[item.icon] || iconMap.folder}</span>
                    {showLabels && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                  {showLabels && isOpen && (
                    <div className="ml-2 mt-0.5 pl-3 border-l-2 border-gray-100 space-y-0.5">
                      {projectsSubItems.map((sub) => {
                        const isActive = location.pathname === sub.href;
                        return (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-500 -ml-[2px] pl-[10px]'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-gray-500 shrink-0">{iconMap[sub.icon]}</span>
                            <span className="flex-1 truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            if (item.hasSub && item.label === 'Settings') {
              const isOpen = settingsOpen && showLabels;
              return (
                <div key={item.label} className="mb-0.5">
                  <button
                    type="button"
                    onClick={() => setSettingsOpen(!settingsOpen)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg w-full transition-colors text-left ${
                      isSettingsPath ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-gray-500 shrink-0">{iconMap[item.icon] || iconMap.grid}</span>
                    {showLabels && (
                      <>
                        <span className="flex-1 truncate">{item.label}</span>
                        <svg
                          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </>
                    )}
                  </button>
                  {showLabels && isOpen && (
                    <div className="ml-2 mt-0.5 pl-3 border-l-2 border-gray-100 space-y-0.5">
                      {settingsSubItems.map((sub) => {
                        const isActive = location.pathname === sub.href;
                        return (
                          <Link
                            key={sub.label}
                            to={sub.href}
                            className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                              isActive
                                ? 'bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-500 -ml-[2px] pl-[10px]'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <span className="text-gray-500 shrink-0">{iconMap[sub.icon]}</span>
                            <span className="flex-1 truncate">{sub.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const isDashboardActive = item.href === '/dashboard' && location.pathname === '/dashboard';
            const isInventoryActive = item.href === '/dashboard/inventory' && location.pathname === '/dashboard/inventory';
            const isReportsActive = item.href === '/dashboard/reports' && location.pathname === '/dashboard/reports';
            return (
              <Link
                key={item.label}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-colors ${
                  isDashboardActive || isInventoryActive || isReportsActive ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className="text-gray-500 shrink-0">{iconMap[item.icon] || iconMap.grid}</span>
                {showLabels && (
                  <>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.hasSub && (
                      <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-3 sm:px-4 shrink-0 gap-2">
          <button
            type="button"
            onClick={() => setMobileMenuOpen((o) => !o)}
            className="md:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Open menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:flex p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Toggle sidebar"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex-1 min-w-0 max-w-xl mx-2 sm:mx-4">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="search"
                placeholder="Search..."
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 outline-none text-sm"
              />
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button type="button" className="hidden sm:flex p-2 rounded-lg text-gray-500 hover:bg-gray-100" aria-label="Help">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 relative">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </button>
            <div className="relative" ref={companyDropdownRef}>
              <button
                type="button"
                onClick={() => setCompanyDropdown(!companyDropdown)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-100"
              >
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-sm font-medium max-w-[120px] truncate hidden sm:inline">{companyName}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {companyDropdown && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-50">
                  <button
                    type="button"
                    onClick={() => { setCompanyDropdown(false); handleLogout(); }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
                  >
                    Switch Company
                  </button>
                  <div className="px-4 py-2 text-sm text-gray-900 font-medium border-t border-gray-100">{companyName}</div>
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              className="w-9 h-9 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center font-semibold text-sm hover:bg-brand-200 transition-colors"
              title="Logout"
            >
              {userInitial}
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 min-h-0 p-3 sm:p-4 overflow-hidden flex flex-col">
          <Routes>
            <Route index element={<DashboardIndexWrapper allowedModules={allowedModules} scale={scale} />} />
            <Route path="accounting/chart-of-accounts" element={<ChartOfAccountsPage />} />
            <Route path="accounting/journal-entries" element={<JournalEntriesPage />} />
            <Route path="accounting/general-ledger" element={<GeneralLedgerPage />} />
            <Route path="accounting/bank-cash" element={<BankCashPage />} />
            <Route path="sales/customers" element={<CustomersPage />} />
            <Route path="sales/invoices" element={<InvoicesPage />} />
            <Route path="sales/payments-received" element={<PaymentsReceivedPage />} />
            <Route path="purchases/vendors" element={<VendorsPage />} />
            <Route path="purchases/bills" element={<BillsPage />} />
            <Route path="purchases/payments-made" element={<PaymentsMadePage />} />
            <Route path="inventory" element={<InventoryPage />} />
            <Route path="hr/employees" element={<EmployeesPage />} />
            <Route path="hr/attendance" element={<AttendancePage />} />
            <Route path="hr/payroll" element={<PayrollPage />} />
            <Route path="hr/settings" element={<LeaveCreditSettingsPage />} />
            <Route path="projects/all" element={<AllProjectsPage />} />
            <Route path="projects/planning" element={<ProjectPlanningPage />} />
            <Route path="projects/team-composition" element={<TeamCompositionPage />} />
            <Route path="projects/monitoring" element={<ProjectMonitoringPage />} />
            <Route path="projects/task-tracker" element={<TaskTrackerPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settings/general" element={<GeneralPage />} />
            <Route path="settings/access-control" element={<AccessControlPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
